import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Processing Modal Component
 *
 * Displays processing state with spinner, success, or error states.
 * Used during brand profile submission and LLM processing.
 */
export default function ProcessingModal({ error, onRetry, onCancel }) {
  const { t } = useTranslation('brandProfile')

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        {error ? (
          // Error State
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('wizard.processing.errorTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {error}
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onRetry}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                {t('wizard.processing.retry')}
              </button>
            </div>
          </div>
        ) : (
          // Loading State
          <div className="text-center">
            {/* Animated Spinner */}
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('wizard.processing.title')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('wizard.processing.subtitle')}
            </p>

            {/* Processing Steps Indicator */}
            <div className="space-y-2 text-left max-w-xs mx-auto">
              <ProcessingStep
                label={t('wizard.processing.steps.validating')}
                status="completed"
              />
              <ProcessingStep
                label={t('wizard.processing.steps.sanitizing')}
                status="active"
              />
              <ProcessingStep
                label={t('wizard.processing.steps.compressing')}
                status="pending"
              />
              <ProcessingStep
                label={t('wizard.processing.steps.saving')}
                status="pending"
              />
            </div>

            {/* Cancel button (disabled during processing to prevent data loss) */}
            <p className="mt-6 text-xs text-gray-400">
              {t('wizard.processing.doNotClose')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Processing Step Indicator
 */
function ProcessingStep({ label, status }) {
  return (
    <div className="flex items-center space-x-3">
      {/* Status Icon */}
      <div className="flex-shrink-0">
        {status === 'completed' && (
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {status === 'active' && (
          <div className="w-5 h-5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
        )}
        {status === 'pending' && (
          <div className="w-5 h-5 rounded-full bg-gray-200" />
        )}
      </div>

      {/* Label */}
      <span
        className={`text-sm ${
          status === 'completed'
            ? 'text-green-600'
            : status === 'active'
            ? 'text-blue-600 font-medium'
            : 'text-gray-400'
        }`}
      >
        {label}
      </span>
    </div>
  )
}
